// Leader progress track: an ambient 8px inline-SVG bar directly under the
// live epoch strip, spanning the running epoch 0..slots_in_epoch. The tick
// set is exactly the served /schedule?epoch slots array (no interpolation,
// no expected-fee synthesis); the cursor is the served head position. Pure
// builders + one DOM renderer, mirroring live-strip.ts. aria-hidden: the
// strip's text already carries the facts; V1 is title-less and
// tooltip-less.

import type { ScheduleSlot, ScheduleSlotStatus } from "./api";

/**
 * Display category of a served slot status. skipped/unavailable/abandoned
 * share the failure ink (--vf-error, the one sanctioned red): each is a
 * leader slot with no observed block — a factual failure mark, not a
 * judgment. scheduled (future) renders muted at reduced opacity.
 */
export type TickCategory = "produced" | "failed" | "scheduled";

export interface TrackTick {
  /** Tick position: run-start slot index / slots_in_epoch x 100. */
  xPct: number;
  category: TickCategory;
}

export function tickCategory(status: ScheduleSlotStatus): TickCategory {
  switch (status) {
    case "produced":
      return "produced";
    case "scheduled":
      return "scheduled";
    case "skipped":
    case "unavailable":
    case "abandoned":
      return "failed";
  }
}

/**
 * Served slots to ticks. Runs of CONSECUTIVE slots sharing a category
 * (the 4-slot leader rotations) merge into one tick at the run's start —
 * they are sub-pixel apart at epoch scale. A mixed-status rotation is NOT
 * merged: it yields one tick per same-category run, so a failure inside a
 * rotation always keeps its own mark (the renderer paints failure ink
 * last). Slots outside [startSlot, startSlot + slotsInEpoch) are dropped.
 */
export function buildTrackTicks(
  slots: readonly ScheduleSlot[],
  startSlot: number,
  slotsInEpoch: number,
): TrackTick[] {
  const sorted = [...slots].sort((a, b) => a.slot - b.slot);
  const ticks: TrackTick[] = [];
  let runStart = -1;
  let runPrev = -1;
  let runCategory: TickCategory | null = null;
  const flush = (): void => {
    if (runCategory !== null) {
      ticks.push({
        xPct: ((runStart - startSlot) / slotsInEpoch) * 100,
        category: runCategory,
      });
    }
  };
  for (const s of sorted) {
    const idx = s.slot - startSlot;
    if (idx < 0 || idx >= slotsInEpoch) {
      continue;
    }
    const category = tickCategory(s.status);
    if (category === runCategory && s.slot === runPrev + 1) {
      runPrev = s.slot;
      continue;
    }
    flush();
    runStart = s.slot;
    runPrev = s.slot;
    runCategory = category;
  }
  flush();
  return ticks;
}

// --- DOM renderer ---------------------------------------------------------

const SVG_NS = "http://www.w3.org/2000/svg";

const CATEGORY_CLASS: Record<TickCategory, string> = {
  produced: "vf-track-produced",
  failed: "vf-track-failed",
  scheduled: "vf-track-scheduled",
};

/** Paint order: failure ink last so an overlapping mixed rotation never hides a failure; the cursor paints above everything. */
const PAINT_ORDER: readonly TickCategory[] = ["scheduled", "produced", "failed"];

function rect(cls: string, xPct: number, y: number, height: number, width = 1): SVGRectElement {
  const r = document.createElementNS(SVG_NS, "rect");
  r.setAttribute("class", cls);
  r.setAttribute("x", `${xPct.toFixed(4)}%`);
  r.setAttribute("y", String(y));
  r.setAttribute("width", String(width));
  r.setAttribute("height", String(height));
  return r;
}

/**
 * Rebuilds the track SVG: hairline baseline, 1px ticks at proportional x,
 * and the 2px "now" cursor stripe in the positive green (--vf-policy-note
 * — it marks OUR position, a positive fact; phase 3, operator direction).
 * blinkCursor adds the pulse class (imminent leader slot on fresh data;
 * the caller computes the condition — presentation only, no layout
 * shift). cursorPct null (no facts at all) renders nothing — the
 * container's :empty rule collapses it. An epoch roll with no timeline
 * yet renders the bare track + cursor (empty ticks), never stale ticks.
 */
export function renderLiveTrack(
  el: HTMLElement,
  cursorPct: number | null,
  ticks: readonly TrackTick[],
  blinkCursor = false,
): void {
  el.replaceChildren();
  if (cursorPct === null) {
    return;
  }
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "vf-track");
  svg.setAttribute("width", "100%");
  svg.setAttribute("height", "8");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const baseline = document.createElementNS(SVG_NS, "rect");
  baseline.setAttribute("class", "vf-track-line");
  baseline.setAttribute("x", "0");
  baseline.setAttribute("y", "4");
  baseline.setAttribute("width", "100%");
  baseline.setAttribute("height", "1");
  svg.append(baseline);

  for (const category of PAINT_ORDER) {
    for (const tick of ticks) {
      if (tick.category === category) {
        svg.append(rect(`vf-track-tick ${CATEGORY_CLASS[category]}`, tick.xPct, 1, 6));
      }
    }
  }

  const clamped = Math.min(100, Math.max(0, cursorPct));
  const cursorClass = blinkCursor ? "vf-track-cursor vf-track-cursor-blink" : "vf-track-cursor";
  svg.append(rect(cursorClass, clamped, 0, 8, 2));
  el.append(svg);
}
